import React, { useEffect, useRef } from 'react';

export function NeuralNetworkBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    let animationFrameId;
    let handleMouseMove, handleMouseLeave, handleResize;
    let active = true;

    // Delay start of calculation/animation to keep the main thread free for initial load & paint
    const timer = setTimeout(() => {
      if (!active) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      let particles = [];
      let width = (canvas.width = window.innerWidth);
      let height = (canvas.height = window.innerHeight);

      // Mouse tracking
      const mouse = {
        x: null,
        y: null,
        radius: 150, // interaction radius
      };

      handleMouseMove = (e) => {
        mouse.x = e.clientX;
        mouse.y = e.clientY;
      };

      handleMouseLeave = () => {
        mouse.x = null;
        mouse.y = null;
      };

      handleResize = () => {
        if (!canvas) return;
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
        init();
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('resize', handleResize);

      // Particle class
      class Particle {
        constructor(x, y) {
          this.x = x;
          this.y = y;
          this.size = Math.random() * 2 + 1; // particle size: 1px to 3px
          this.baseSpeedX = (Math.random() - 0.5) * 0.4; // very slow speed
          this.baseSpeedY = (Math.random() - 0.5) * 0.4;
          this.vx = this.baseSpeedX;
          this.vy = this.baseSpeedY;
        }

        update() {
          // Normal movement
          this.x += this.vx;
          this.y += this.vy;

          // Bounce on boundaries
          if (this.x < 0 || this.x > width) this.vx = -this.vx;
          if (this.y < 0 || this.y > height) this.vy = -this.vy;

          // Interactive mouse push effect
          if (mouse.x !== null && mouse.y !== null) {
            const dx = this.x - mouse.x;
            const dy = this.y - mouse.y;
            const distance = Math.hypot(dx, dy);

            if (distance < mouse.radius) {
              const force = (mouse.radius - distance) / mouse.radius;
              // Push gently away from mouse
              const angle = Math.atan2(dy, dx);
              this.x += Math.cos(angle) * force * 1.5;
              this.y += Math.sin(angle) * force * 1.5;
            }
          }
        }

        draw() {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          // Luxury gold particle fill
          ctx.fillStyle = 'rgba(201, 162, 39, 0.25)';
          ctx.fill();
        }
      }

      // Initialize particles based on screen size
      function init() {
        particles = [];
        // Adjust density for mobile vs desktop - reduced by 28% for elegance and readability
        const particleCount = Math.floor((width * height) / 25000);
        const cappedCount = Math.min(Math.max(particleCount, 20), 80); // cap between 20 and 80 for performance

        for (let i = 0; i < cappedCount; i++) {
          const x = Math.random() * width;
          const y = Math.random() * height;
          particles.push(new Particle(x, y));
        }
      }

      // Connect particles with lines
      function connect() {
        const maxDistance = 120;
        for (let a = 0; a < particles.length; a++) {
          for (let b = a + 1; b < particles.length; b++) {
            const dx = particles[a].x - particles[b].x;
            const dy = particles[a].y - particles[b].y;
            const distance = Math.hypot(dx, dy);

            if (distance < maxDistance) {
              // Opacity based on distance
              const alpha = (1 - distance / maxDistance) * 0.08;
              ctx.strokeStyle = `rgba(201, 162, 39, ${alpha})`;
              ctx.lineWidth = 0.8;
              ctx.beginPath();
              ctx.moveTo(particles[a].x, particles[a].y);
              ctx.lineTo(particles[b].x, particles[b].y);
              ctx.stroke();
            }
          }

          // Draw connections to mouse cursor
          if (mouse.x !== null && mouse.y !== null) {
            const dx = particles[a].x - mouse.x;
            const dy = particles[a].y - mouse.y;
            const distance = Math.hypot(dx, dy);

            if (distance < mouse.radius) {
              const alpha = (1 - distance / mouse.radius) * 0.12;
              ctx.strokeStyle = `rgba(201, 162, 39, ${alpha})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(particles[a].x, particles[a].y);
              ctx.lineTo(mouse.x, mouse.y);
              ctx.stroke();
            }
          }
        }
      }

      // Animation Loop
      function animate() {
        ctx.clearRect(0, 0, width, height);

        particles.forEach((particle) => {
          particle.update();
          particle.draw();
        });

        connect();

        animationFrameId = requestAnimationFrame(animate);
      }

      init();
      animate();
    }, 1500);

    return () => {
      active = false;
      clearTimeout(timer);
      if (handleMouseMove) window.removeEventListener('mousemove', handleMouseMove);
      if (handleMouseLeave) window.removeEventListener('mouseleave', handleMouseLeave);
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-[1]"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
